import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import LocalizedLink from '../LocalizedLink/LocalizedLink';
import { LOCALES, localizePath, parseLocalePath } from '../../seo/site';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { trackLanguageChange } from '../../firebase/config';

const Header = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const languages = LOCALES;
  const { pagePath } = parseLocalePath(location.pathname);

  const navigation = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.services'), href: '/services' },
    { name: t('nav.destinations'), href: '/destinations' },
    { name: t('nav.cargo'), href: '/cargo' },
    { name: t('nav.whyUs'), href: '/why-us' }
  ];

  const changeLanguage = (lng) => {
    trackLanguageChange(lng);
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <LocalizedLink to="/" className="flex items-center">
            <div className="text-2xl font-bold text-orange-accent">
              K&B<span className="text-black">Trans</span>
            </div>
          </LocalizedLink>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-4 xl:space-x-8">
            {navigation.map((item) => (
              <LocalizedLink
                key={item.name}
                to={item.href}
                className={`text-xs xl:text-sm font-medium transition-colors duration-200 hover:text-orange-accent whitespace-nowrap ${
                  pagePath === item.href
                    ? 'text-orange-accent'
                    : 'text-gray-700'
                }`}
              >
                {item.name}
              </LocalizedLink>
            ))}
          </nav>

          {/* Contact Info & Language Switcher */}
          <div className="hidden lg:flex items-center space-x-4 xl:space-x-6">
            {/* Contact Info */}
            <div className="flex items-center space-x-2 xl:space-x-4">
              <a 
                href="https://wa.me/48577083279" 
                className="flex items-center space-x-2 text-xs xl:text-sm text-gray-700 hover:text-orange-accent transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.531 3.542z"/>
                </svg>
                <span>+48 577 083 279</span>
              </a>
              <a 
                href="mailto:info@kbtrans.eu" 
                className="flex items-center space-x-2 text-xs xl:text-sm text-gray-700 hover:text-orange-accent transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>info@kbtrans.eu</span>
              </a>
            </div>

            {/* Language Switcher */}
            <div className="flex items-center space-x-1">
              {languages.map((lang) => (
                <Link
                  key={lang.code}
                  to={localizePath(`${location.pathname}${location.search}${location.hash}`, lang.code)}
                  hrefLang={lang.code}
                  lang={lang.code}
                  aria-label={lang.name}
                  aria-current={i18n.language === lang.code ? 'true' : undefined}
                  onClick={() => changeLanguage(lang.code)}
                  className={`px-2 xl:px-3 py-1 rounded-md text-xs xl:text-sm font-medium transition-colors duration-200 ${
                    i18n.language === lang.code
                      ? 'bg-orange-accent text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="mr-1">{lang.flag}</span>
                  {lang.code.toUpperCase()}
                </Link>
              ))}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            aria-label={t('seo.menu')}
            aria-expanded={isMenuOpen}
            className="lg:hidden flex items-center justify-center w-8 h-8 rounded-md text-gray-700 hover:text-orange-accent transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden border-t border-gray-200 py-4"
          >
            <div className="flex flex-col space-y-4">
              {navigation.map((item) => (
                <LocalizedLink
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={`text-base font-medium transition-colors duration-200 hover:text-orange-accent ${
                    pagePath === item.href
                      ? 'text-orange-accent'
                      : 'text-gray-700'
                  }`}
                >
                  {item.name}
                </LocalizedLink>
              ))}
              
              {/* Mobile Contact Info */}
              <div className="pt-4 border-t border-gray-200">
                <a 
                  href="https://wa.me/48577083279" 
                  className="flex items-center space-x-2 text-sm text-gray-700 hover:text-orange-accent transition-colors mb-2"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.531 3.542z"/>
                  </svg>
                  <span>+48 577 083 279</span>
                </a>
                <a 
                  href="mailto:info@kbtrans.eu" 
                  className="flex items-center space-x-2 text-sm text-gray-700 hover:text-orange-accent transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>info@kbtrans.eu</span>
                </a>
              </div>

              {/* Mobile Language Switcher */}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  {languages.map((lang) => (
                    <Link
                      key={lang.code}
                      to={localizePath(`${location.pathname}${location.search}${location.hash}`, lang.code)}
                      hrefLang={lang.code}
                      lang={lang.code}
                      aria-label={lang.name}
                      aria-current={i18n.language === lang.code ? 'true' : undefined}
                      onClick={() => changeLanguage(lang.code)}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-colors duration-200 ${
                        i18n.language === lang.code
                          ? 'bg-orange-accent text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <span className="mr-1">{lang.flag}</span>
                      {lang.code.toUpperCase()}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </header>
  );
};

export default Header; 